import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as XSellLoader } from 'images/x-sell-loader.svg';
import StateContainer from 'components/StateContainer';
import { getApplicationData } from 'selectors/getApplicationData';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData as getApplicationDataThunk, verifyApplication } from 'thunks';
import { FlowComponentType } from 'routes/types';
import { VerifyStatus } from 'api/LoanOfferApi';
import { VerificationNavigationResult } from 'enums/FlowNextResults';
import { isManualReviewKycStatus, KycStatus } from 'handlers/kyc';
import { resolveVerificationRedirect, VerificationRedirect } from 'components/Verification/resolveVerificationRedirect';
import analyzingStyles from 'components/FinancialCheckup/Analyzing/Analyzing.module.scss';

const POLL_INTERVAL_MS = 10_000;
const POLL_DURATION_MS = 3 * 60 * 1000;
const FETCH_TIMEOUT_MS = 15_000;

const VerifyingKyc = ({ handleNext }: FlowComponentType): JSX.Element => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application } = useSelector(getApplicationData);
  const applicationId = application!.id;
  const [progress, setProgress] = useState(10);

  useEffect(() => {
    const startedAt = Date.now();
    let cancelled = false;
    let advanced = false;
    let sleepTimeoutId = 0;
    let resolveSleep: (() => void) | undefined;

    const advance = (nextResult: VerifyStatus | VerificationNavigationResult) => {
      if (cancelled || advanced) {
        return;
      }
      advanced = true;
      handleNext(nextResult);
    };

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        resolveSleep = resolve;
        sleepTimeoutId = window.setTimeout(() => {
          resolveSleep = undefined;
          resolve();
        }, ms);
      });

    const fetchApplicationData = async () => {
      let timeoutId = 0;
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error('KYC status request timed out'));
        }, FETCH_TIMEOUT_MS);
      });

      try {
        const response = await Promise.race([dispatchWithUnwrap(getApplicationDataThunk(applicationId)), timeout]);
        return response;
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const runVerify = async (kycStatus?: string) => {
      try {
        const { iovation_blackbox: iovationBlackbox } = window as any;
        const result = await dispatchWithUnwrap(
          verifyApplication({ applicationId, kycVariables: { iovationBlackbox: iovationBlackbox || '' } }),
        );
        analytics.track('Verify Result', result);
        const nextResult = resolveVerificationRedirect(result, kycStatus);
        if (nextResult === VerificationRedirect.WaitForKyc) {
          return;
        }
        advance(nextResult);
      } catch {
        if (Date.now() - startedAt >= POLL_DURATION_MS) {
          advance(VerifyStatus.ManualVerify);
        }
      }
    };

    // Each status read finishes before the next one starts, so a response that
    // lands after the window still chooses verify instead of manual review.
    const poll = async () => {
      while (!cancelled && !advanced) {
        if (Date.now() - startedAt >= POLL_DURATION_MS) {
          advance(VerifyStatus.ManualVerify);
          return;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchApplicationData();
          if (cancelled || advanced) {
            return;
          }
          const kycStatus = response.application.kycStatus ?? '';
          if (kycStatus === KycStatus.PendingDocuments) {
            advance(VerifyStatus.PendingDocuments);
            return;
          }
          if (isManualReviewKycStatus(kycStatus)) {
            advance(VerifyStatus.ManualVerify);
            return;
          }
          if (kycStatus === KycStatus.Approved) {
            // eslint-disable-next-line no-await-in-loop
            await runVerify(kycStatus);
          }
        } catch {
          // Keep polling until the wait window ends.
        }

        if (cancelled || advanced) {
          return;
        }

        const remainingMs = POLL_DURATION_MS - (Date.now() - startedAt);
        if (remainingMs <= 0) {
          advance(VerifyStatus.ManualVerify);
          return;
        }

        // eslint-disable-next-line no-await-in-loop
        await wait(Math.min(POLL_INTERVAL_MS, remainingMs));
      }
    };

    poll();

    const progressInterval = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      setProgress(Math.min(90, 10 + (elapsedMs / POLL_DURATION_MS) * 80));
    }, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(sleepTimeoutId);
      resolveSleep?.();
      window.clearInterval(progressInterval);
    };
  }, []);

  return (
    <div className={analyzingStyles.container}>
      <StateContainer icon={<XSellLoader />} progress={progress} title="We are verifying your information" />
    </div>
  );
};

export default VerifyingKyc;
